import { createTheme } from '@material-ui/core';

/*
  J1 New UI Kit treatment (CS-766) on MUI v4.

  The look is driven by the CSS custom properties in css/tokens.css (verbatim
  from web-app PR #1311) + css/kit.css. JSS passes `var(...)` strings straight
  through to the stylesheet, so overrides reference tokens wherever possible.

  MUI's palette computes derived colors (hover ripples, contrast text) at
  theme-creation time, so palette entries need raw hex values. These mirror
  the semantic aliases in tokens.css — if a token value changes upstream,
  update the matching hex here.
*/
export const kit = {
  brand: '#cdea68', // --brand (= --lime-400) — brand accent, NOT the product CTA
  brandForeground: '#040f0b', // ink on lime (= --eclipse-900)
  primary: '#040f0b', // --primary (= --eclipse-900)
  primaryForeground: '#ffffff', // --primary-foreground
  error: '#dc3d2a', // --error (= --red-500)
  warning: '#ff5d2e', // --warning (= --orange-500)
  success: '#15a06b', // --success (= --green-500)
  fgHeading: '#0e1612', // --fg-heading
  fgBody: '#3b4441', // --fg-body
  fgMuted: '#5a615e', // --fg-muted
};

/* The kit's focus treatment: ring-color at 50%, 3px (Button/Input specs) */
const focusRing = 'color-mix(in srgb, var(--ring) 50%, transparent)';

export const theme = createTheme({
  palette: {
    primary: {
      // Product CTA is black ink (--primary = --eclipse-900), NOT lime. The
      // kit's Lime is a brand accent (--brand), not the confirm surface —
      // matches web-app's Button `default` variant (bg-primary).
      main: kit.primary,
      contrastText: kit.primaryForeground,
    },
    error: { main: kit.error },
    warning: { main: kit.warning },
    success: { main: kit.success },
    text: {
      primary: kit.fgBody,
      secondary: kit.fgMuted,
    },
  },
  typography: {
    fontFamily: 'var(--font-sans)',
    // --text-body 16/23
    body1: { fontSize: '1rem', lineHeight: 1.4375 },
    // --text-label 14/20
    body2: { fontSize: '0.875rem', lineHeight: 1.4286 },
    subtitle1: { fontSize: '0.875rem', lineHeight: 1.4286, fontWeight: 500 },
    // --text-caption 12.5/17
    subtitle2: {
      fontSize: '0.78125rem',
      lineHeight: 1.36,
      fontWeight: 400,
    },
    // --text-heading-2 20/26, headings are semibold in the kit
    h5: { fontSize: '1.25rem', lineHeight: 1.3, fontWeight: 600 },
  },
  props: {
    // Kit surfaces are Flat (border, no shadow) unless explicitly raised
    MuiPaper: { elevation: 0 },
    // No material ripple — kit buttons switch state instantly
    MuiButtonBase: { disableRipple: true },
    MuiTooltip: { TransitionProps: { timeout: 0 } },
  },
  overrides: {
    MuiButton: {
      root: {
        // Button spec: h-9 rounded-md text-sm font-medium
        minHeight: 36,
        borderRadius: 'var(--radius-md)',
        padding: '8px 16px',
        fontSize: 'var(--text-label)',
        lineHeight: 'var(--text-label--line-height)',
        fontWeight: 500,
        textTransform: 'none',
        // Hover states switch instantly — no transition
        transition: 'none',
        '&:focus-visible': {
          boxShadow: `0 0 0 3px ${focusRing}`,
        },
      },
      contained: {
        // Kit primary button is Flat (shadow-xs → none), not raised
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
        '&:active': { boxShadow: 'none' },
        '&$disabled': {
          backgroundColor: 'var(--primary)',
          color: 'var(--primary-foreground)',
          opacity: 0.5,
        },
      },
      containedPrimary: {
        // Product CTA: black ink fill with white text, no border, hover dims to
        // primary/90 — mirrors web-app Button `default`
        // (bg-primary text-primary-foreground hover:bg-primary/90).
        backgroundColor: 'var(--primary)',
        color: 'var(--primary-foreground)',
        '&:hover': {
          backgroundColor:
            'color-mix(in srgb, var(--primary) 90%, transparent)',
        },
      },
      outlined: {
        // outline variant: border + bg-background, hover bg-accent, no shadow
        border: '1px solid var(--border)',
        backgroundColor: 'var(--background)',
        color: 'var(--fg-heading)',
        '&:hover': {
          backgroundColor: 'var(--accent)',
          border: '1px solid var(--border)',
        },
      },
      text: {
        // ghost variant: transparent, hover bg-accent
        color: 'var(--fg-heading)',
        '&:hover': { backgroundColor: 'var(--accent)' },
      },
    },
    MuiOutlinedInput: {
      // Input spec: h-9 rounded-md border-input bg-transparent, Flat (no
      // shadow); focus = border-ring + inset 3px ring at ring/50
      root: {
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'transparent',
        '&$focused $notchedOutline': {
          borderColor: 'var(--ring)',
          borderWidth: 1,
        },
        '&$focused': {
          boxShadow: `inset 0 0 0 3px ${focusRing}`,
        },
        '&$error $notchedOutline': {
          borderColor: 'var(--destructive)',
        },
      },
      notchedOutline: {
        borderColor: 'var(--input)',
      },
      input: {
        // h-9 with px-3: 36px total at 14px control type
        padding: '8px 12px',
        height: 20,
        fontSize: 'var(--text-control-md)',
      },
    },
    MuiFormHelperText: {
      root: {
        fontSize: 'var(--text-caption)',
        lineHeight: 'var(--text-caption--line-height)',
        marginTop: 8,
        color: 'var(--fg-muted)',
        '&$error': { color: 'var(--error)' },
      },
    },
    MuiLinearProgress: {
      root: {
        height: 4,
        borderRadius: 9999,
      },
      colorPrimary: { backgroundColor: 'var(--bg-3)' },
      barColorPrimary: { backgroundColor: 'var(--primary)' },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: 'var(--primary)',
        color: 'var(--primary-foreground)',
        fontSize: 'var(--text-caption)',
        borderRadius: 'var(--radius-sm)',
        padding: '6px 10px',
        boxShadow: 'var(--shadow-hover)',
      },
    },
    MuiPaper: {
      rounded: { borderRadius: 'var(--radius-md)' },
    },
    MuiDivider: {
      root: { backgroundColor: 'var(--border)' },
    },
    MuiCard: {
      // Kit Card primitive: rounded-xl border shadow-sm
      root: {
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-card)',
      },
    },
    MuiCircularProgress: {
      colorPrimary: { color: 'var(--primary)' },
    },
  },
});
